/**
 * UC-007 multi-account FIO admin (DEC-006) — wire types + banner helper.
 *
 * Banner gate (REQ-EH-006): zobrazit "FIO API nedostupné" pokud
 * `last_synced_at` chybí nebo je starší než 2 hodiny od `now`. Princip #4
 * (sync transparency) — uživatel musí vědět, kdy byla data naposled svěží.
 */

export interface PaymentAccountRow {
  account_id: string;
  fio_account_number: string;
  mode: string;
  label: string;
  has_token: boolean;
  token_kid: string | null;
  last_synced_at: string | null;
  created_at: string;
  deactivated: boolean;
}

export interface PaymentAccountListResponse {
  accounts: PaymentAccountRow[];
}

/** PaymentAccount.mode — mirror BE enum (`backend/.../PaymentAccountTable.kt`). */
export type PaymentAccountMode = "personal" | "corporate" | "internal";

export const PAYMENT_ACCOUNT_MODE_OPTIONS: ReadonlyArray<{
  value: PaymentAccountMode;
  label: string;
  hint: string;
}> = [
  {
    value: "personal",
    label: "Osobní (S1 P2P)",
    hint: "Petr / soukromý FIO účet — výběr příchozích plateb od kamarádů.",
  },
  {
    value: "corporate",
    label: "Firemní (S0/S2 B2B)",
    hint: "Expertní služby s.r.o. / firemní FIO účet — refakturace externím subjektům.",
  },
  {
    value: "internal",
    label: "Interní",
    hint: "Servisní / přípravný účet, neslouží přímo k pairing flow.",
  },
];

export interface CreatePaymentAccountPayload {
  fio_account_number: string;
  mode: PaymentAccountMode;
  label: string;
  fio_api_token?: string | null;
}

export interface CreatePaymentAccountResponse {
  account_id: string;
}

export interface UpdatePaymentAccountPayload {
  label: string;
  mode: PaymentAccountMode;
  /** Volitelná rotace tokenu — prázdné/null = token nechat beze změny. */
  fio_api_token: string | null;
}

export interface RotateTokenPayload {
  fio_api_token: string;
}

export interface RotateTokenResponse {
  account_id: string;
  new_kid: string;
  rotated_at: string;
}

export interface SyncPaymentAccountResponse {
  account_id: string;
  outcome: string;
  fetched: number;
  ingested: number;
  paired: number;
  cross_account_flagged: number;
}

/**
 * Jedna stažená FIO transakce (UC-007 viditelnost výsledku syncu).
 * `amount_kc` = celé jednotky měny (haléře/centy truncate, princip #2 — žádný FX).
 * `counter_party_*` jsou PII — vrací je jen globalAdmin endpoint.
 */
export interface FioTransactionRow {
  id: string;
  fio_id: number;
  amount_kc: number;
  currency: string;
  variable_symbol: string | null;
  counter_party_name: string | null;
  counter_party_account: string | null;
  tx_datetime: string;
  cross_account_anomaly: boolean;
  paired: boolean;
  paired_intent_status: string | null;
}

export interface FioTransactionListResponse {
  account_id: string;
  transactions: FioTransactionRow[];
  /** true = dosažen limit (zobrazena jen poslední dávka). */
  truncated: boolean;
}

export const FIO_STALE_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2h per REQ-EH-006

/**
 * True když `last_synced_at` chybí (account ještě nikdy nesync-oval) nebo je
 * starší než REQ-EH-006 práh (2h od `now`). False = banner se neukazuje.
 */
export function isFioApiStale(
  lastSyncedAt: string | null,
  now: Date = new Date(),
): boolean {
  if (lastSyncedAt === null || lastSyncedAt.length === 0) return true;
  const ts = Date.parse(lastSyncedAt);
  if (Number.isNaN(ts)) return true;
  return now.getTime() - ts > FIO_STALE_THRESHOLD_MS;
}
